import Logo from "@/components/Logo";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { useTranslation } from "react-i18next";

export default function Footer() {
  const { t } = useTranslation("home");
  const { openModal } = useCookieConsent();

  const informatieLinks = [
    { label: "Algemene Voorwaarden", href: "/algemene-voorwaarden/" },
    { label: "Privacybeleid", href: "/privacybeleid/" },
    { label: "Cookiebeleid", href: "/cookiebeleid/" },
    { label: "Cookievoorkeuren", href: "#", action: openModal },
    { label: "Blog", href: "/blog/" },
    { label: "Sitemap", href: "/sitemap.xml" },
    { label: "Contact", href: "/contact/" },
  ];

  const dienstenLinks = [
    { label: "Ramen reparatie", href: "/ramen-reparatie/" },
    { label: "Kozijnreparatie", href: "/kozijn-reparatie/" },
    { label: "Schuifpui reparatie", href: "/schuifpui-reparatie/" },
    { label: "Draaikiepraam reparatie", href: "/draaikiepraam-reparatie/" },
    { label: "Glas vervangen", href: "/glas-vervangen/" },
    { label: "Slot- en cilindervervanging", href: "/slot-cilinder-vervangen/" },
    { label: "Velux & dakraam reparatie", href: "/velux-reparatie/" },
    { label: "Inbraakschade reparatie", href: "/inbraakschade-reparatie/" },
    { label: "Raamkruk vervangen", href: "/raamkruk-vervangen/" },
    { label: "Slotenmaker 24/7", href: "/slotenmaker-eindhoven/" },
  ];

  const wijkenLinks = [
    { label: "Strijp", href: "/wijken/strijp/" },
    { label: "Woensel", href: "/wijken/woensel/" },
    { label: "Centrum", href: "/wijken/centrum/" },
    { label: "Tongelre", href: "/wijken/tongelre/" },
    { label: "Gestel", href: "/wijken/gestel/" },
    { label: "Stratum", href: "/wijken/stratum/" },
    { label: "Veldhoven", href: "/wijken/veldhoven/" },
    { label: "Geldrop", href: "/wijken/geldrop/" },
  ];

  return (
    <footer className="px-4 md:px-5 pb-4 md:pb-5 w-full">
      <div className="bg-brand-text rounded-3xl md:rounded-[24px] p-10 md:p-16 lg:p-20">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-10 md:mb-14">
            <Logo variant="footer" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10">
            {/* Col 1 — Informatie */}
            <div>
              <h4 className="font-geist font-bold text-sm text-white uppercase tracking-wider mb-6">
                Informatie
              </h4>
              <div className="flex flex-col gap-1">
                {informatieLinks.map((link) =>
                  "action" in link && link.action ? (
                    <button
                      key={link.label}
                      type="button"
                      onClick={link.action}
                      className="font-geist text-base text-white/70 hover:text-brand-yellow transition-colors text-left cursor-pointer py-2.5 min-h-11"
                    >
                      {link.label}
                    </button>
                  ) : (
                    <a
                      key={link.label}
                      href={link.href}
                      className="font-geist text-base text-white/70 hover:text-brand-yellow transition-colors py-2.5 min-h-11"
                    >
                      {link.label}
                    </a>
                  )
                )}
              </div>
            </div>

            {/* Col 2 — Diensten */}
            <div>
              <h4 className="font-geist font-bold text-sm text-white uppercase tracking-wider mb-6">
                Diensten
              </h4>
              <div className="flex flex-col gap-1">
                {dienstenLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    className="font-geist text-base text-white/70 hover:text-brand-yellow transition-colors py-2.5 min-h-11"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>

            {/* Col 3 — Wijken */}
            <div>
              <h4 className="font-geist font-bold text-sm text-white uppercase tracking-wider mb-6">
                Wijken
              </h4>
              <div className="flex flex-col gap-1">
                {wijkenLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    aria-label={`Diensten in ${link.label}`}
                    className="font-geist text-base text-white/70 hover:text-brand-yellow transition-colors py-2.5 min-h-11"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>

            {/* Col 4 — Contact */}
            <div>
              <h4 className="font-geist font-bold text-sm text-white uppercase tracking-wider mb-6">
                Contact
              </h4>
              <div className="flex flex-col gap-4">
                <a
                  href="tel:+31407987470"
                  className="font-geist-mono text-lg md:text-xl text-white hover:text-brand-yellow transition-colors py-1 min-h-11"
                >
                  040 798 7470
                </a>
                <a
                  href="mailto:info@ramen-repair-eindhoven.nl"
                  className="font-geist-mono text-sm text-white/70 hover:text-brand-yellow transition-colors py-1 min-h-11"
                >
                  info@ramen-repair-eindhoven.nl
                </a>
                <p className="font-geist text-sm text-white/50">
                  Ma — Zo: 08:00 – 20:00
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 md:mt-20 pt-8 border-t border-white/20 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex flex-col items-center sm:items-start gap-2">
              <p className="font-geist text-sm text-white/50">
                © 2026 Ramen Repair
              </p>
              <p className="font-geist text-xs text-white/35 text-center sm:text-left">
                DFPRO — Ondernemingsnummer/BTW: BE 0799.088.572 — Rue Lecat 11/4, 7100 La Louvière (België)
              </p>
            </div>
            <div className="flex items-center gap-4 flex-wrap justify-center">
              <a
                href="/algemene-voorwaarden/"
                className="font-geist text-sm text-white/50 hover:text-brand-yellow transition-colors underline py-2 min-h-11"
              >
                AV
              </a>
              <span className="text-white/30 py-2">·</span>
              <a
                href="/privacybeleid/"
                className="font-geist text-sm text-white/50 hover:text-brand-yellow transition-colors underline py-2 min-h-11"
              >
                Privacy
              </a>
              <span className="text-white/30 py-2">·</span>
              <a
                href="/cookiebeleid/"
                className="font-geist text-sm text-white/50 hover:text-brand-yellow transition-colors underline py-2 min-h-11"
              >
                Cookies
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}